import Badge from '@/components/ui/Badge'
import AppIcon from '@/components/content/app/AppIcon'
import RecommendationBadge from '@/components/ui/RecommendationBadge'
import { Icon } from '@/icons/Icon'
import { useMemo, useRef } from 'react'
import { useMediaQuery } from '@/hooks/useBreakpoint'
import { extractUniqueCriteriaFromApps, extractCriteriaForCategory } from '@/lib/formatting'
import { cn, getBrandColors, scrollToSection, toSlug } from '@/lib/utils'
import { Recommendation } from '@/collections/JunctionTables/AppCategoryRecommendation/constants'
import { checkAppBranding } from '@/lib/formatting'
import type {
  App,
  AppCategoryRecommendation,
  Category,
  CategoryCriteriaCriterion,
  Criterion,
  Review,
  ReviewCategoryCriterionRating,
} from '@/payload-types'

type CompareCriteriaProps = {
  apps: App[]
  focus: string | undefined | null
  categoryId?: Category['id']
  appCategoryEfficiencyPoints: Record<App['id'], number | undefined>
  isPrimary?: boolean
}

// Helper to get rating for a specific app, criterion, and category.
// Scans all populated reviews (preferring published) to handle apps with multiple review objects.
export function getRatingForCriterion(
  app: App,
  criterionId: Criterion['id'],
  categoryId: Category['id'],
): number | null {
  // app.review is a join field that returns { docs: Review[] }
  const reviewDocs = app.review?.docs as Array<Review | number> | undefined
  const populatedReviews = reviewDocs?.filter(
    (r): r is Review => typeof r === 'object' && r !== null,
  )
  if (!populatedReviews?.length) return null

  // Prefer published review first, then fall back to any populated review
  const orderedReviews = [
    ...populatedReviews.filter((r) => r?._status === 'published'),
    ...populatedReviews.filter((r) => r?._status !== 'published'),
  ]

  for (const review of orderedReviews) {
    const ratings = review.reviewCategoryCriterionRating?.docs
    if (!ratings) continue

    for (const ratingDoc of ratings) {
      if (typeof ratingDoc === 'number') continue

      const rating = ratingDoc as ReviewCategoryCriterionRating
      const categoryCriterion = rating.categoryCriterion as CategoryCriteriaCriterion | null
      if (!categoryCriterion || typeof categoryCriterion === 'number') continue

      const criterion = categoryCriterion.criterion
      const category = categoryCriterion.category

      const criterionIdFromRating = typeof criterion === 'number' ? criterion : criterion?.id
      const categoryIdFromRating = typeof category === 'number' ? category : category?.id

      if (criterionIdFromRating === criterionId && categoryIdFromRating === categoryId) {
        if (rating.isApplicable === false) return null
        return rating.rating ?? null
      }
    }
  }

  return null
}

// Determine the winner(s) for a criterion using efficiency-point threshold.
// A rating >= 5 means the app "earned a point" for that criterion.
// - 2+ apps earned → draw (both are competent, regardless of exact scores)
// - Exactly 1 earned → that app wins ("Best")
// - 0 apps earned → draw (no meaningful winner)
// - Single app with a point → "Best" (not a draw)
function determineWinner(
  apps: App[],
  criterionId: Criterion['id'],
  categoryId: Category['id'],
): { winners: App[]; isDraw: boolean } {
  const EFFICIENCY_THRESHOLD = 5

  const ratingsMap: Map<App['id'], number | null> = new Map()
  for (const app of apps) {
    ratingsMap.set(app.id, getRatingForCriterion(app, criterionId, categoryId))
  }

  // Apps that earned an efficiency point (rating >= threshold)
  const appsWithPoint = apps.filter((app) => {
    const rating = ratingsMap.get(app.id)
    return rating !== null && rating !== undefined && rating >= EFFICIENCY_THRESHOLD
  })

  if (appsWithPoint.length === 0) {
    // No app earned a point — draw across all apps
    return { winners: apps, isDraw: apps.length > 1 }
  }

  if (appsWithPoint.length === 1) {
    // Exactly one app earned a point — that app is the winner
    return { winners: appsWithPoint, isDraw: false }
  }

  // 2+ apps earned a point — draw among those that earned
  return { winners: appsWithPoint, isDraw: true }
}

export default function CompareCriteria({
  apps,
  focus,
  categoryId,
  appCategoryEfficiencyPoints,
  isPrimary = false,
}: CompareCriteriaProps) {
  const appsRefs = useRef(apps)
  appsRefs.current = apps
  const isDesktop = useMediaQuery('(min-width: 768px)')
  const criteriaScrollOffset = isDesktop ? 154 : 214

  const criteria = useMemo<Criterion[]>(() => {
    // If categoryId is provided, only get criteria for that category
    if (categoryId !== undefined) {
      return extractCriteriaForCategory(apps, categoryId)
    }
    // Otherwise get all criteria across all categories
    return extractUniqueCriteriaFromApps(apps)
  }, [apps, categoryId])

  if (!criteria || criteria.length === 0) return null

  return (
    <ul className="flex w-full flex-col divide-y divide-gray-200 rounded-xl border border-gray-200 bg-gray-50">
      {criteria.map((criterion) => {
        const criterionId = criterion.id

        // Filter apps that have this criterion (in any category if no categoryId, or in specific category)
        const appResults = apps
          .filter((app) => {
            const appCategoryRecommendations = app.categoryRecommendations?.docs as
              | AppCategoryRecommendation[]
              | undefined

            let hasCriterion = false
            appCategoryRecommendations?.forEach((acr) => {
              const category = acr.category as Category | null
              if (!category) return

              // If categoryId is provided, only check that specific category
              if (categoryId !== undefined && category.id !== categoryId) return

              const ccs = category.categoryCriteria?.docs as CategoryCriteriaCriterion[] | undefined
              ccs?.forEach((cc) => {
                const crit = cc.criterion as Criterion | null
                if (crit && crit.id === criterionId) {
                  hasCriterion = true
                }
              })
            })

            return hasCriterion
          })
          .sort(
            (a, b) =>
              (appCategoryEfficiencyPoints[b.id] ?? 0) - (appCategoryEfficiencyPoints[a.id] ?? 0),
          )

        if (!appResults.length) return null

        // Determine winner based on ratings (only if categoryId is provided)
        const { winners, isDraw } =
          categoryId !== undefined
            ? determineWinner(appResults, criterionId, categoryId)
            : { winners: appResults, isDraw: appResults.length > 1 }

        const focusedApp = apps.find((app) => app.name === focus)

        const handleClick = (id: string) => {
          const childSlug = toSlug(id)
          const parentId = 'editors-verdict'
          const sectionId = focus
            ? `${toSlug(focus)}-${parentId}-${childSlug}`
            : `${parentId}-${childSlug}`
          scrollToSection({
            id: sectionId,
            offset: criteriaScrollOffset,
            animate: true,
            hideHash: true,
          })
        }

        const Row = isPrimary ? 'button' : 'div'

        return (
          <li key={criterion.id}>
            <Row
              {...(isPrimary
                ? {
                    type: 'button' as const,
                    onClick: (event: React.MouseEvent) => {
                      event.preventDefault()
                      handleClick(criterion.name)
                    },
                  }
                : {})}
              className={cn(
                'relative flex h-14 w-full items-center justify-between px-4',
                isPrimary ? 'group cursor-pointer' : '',
              )}
            >
              <div className="flex items-center gap-3">
                {criterion.icon ? <Icon name={criterion.icon} className="text-gray-500" /> : null}
                <span className="text-left font-semibold text-gray-950">{criterion.name}</span>
              </div>

              <CriterionOutcomeDisplay
                winners={winners}
                isDraw={isDraw}
                focus={focusedApp}
                linkToApp={!isPrimary}
                className="transition-transform duration-300 ease-in-out group-hover:-translate-x-7"
              />

              {isPrimary && (
                <div
                  aria-hidden="true"
                  className="absolute right-5 top-1/2 z-20 -translate-y-1/2 rotate-180 text-gray-500 opacity-0 transition-opacity duration-300 group-hover:opacity-100"
                >
                  <Icon name="chevron-left" size={12} />
                </div>
              )}
            </Row>
          </li>
        )
      })}
    </ul>
  )
}

type CriterionOutcomeDisplayProps = {
  winners: App[]
  isDraw: boolean
  focus?: App
  className?: string
  linkToApp?: boolean
}

function CriterionOutcomeDisplay({
  winners,
  isDraw,
  focus,
  className,
  linkToApp = true,
}: CriterionOutcomeDisplayProps) {
  if (winners.length === 1 && !isDraw) {
    // Single winner
    const winner = winners[0]

    // Check if focus is set and focus app is NOT the winner
    const shouldShowFocusAsWeak = focus && focus.id !== winner.id

    const displayApp = shouldShowFocusAsWeak ? focus : winner
    const { backgroundColor } = getBrandColors(displayApp)
    const { appIcon, appGlyph } = checkAppBranding(displayApp)

    return (
      <div className={cn('flex items-center gap-3', className)}>
        <div className="flex items-center gap-2">
          <AppIcon
            icon={appIcon}
            glyph={appGlyph}
            name={displayApp.name}
            slug={linkToApp ? displayApp.slug : undefined}
            background={backgroundColor}
            transition={false}
            size={24}
          />
        </div>

        {shouldShowFocusAsWeak ? (
          <Badge style="disabled" icon="arrow-triangle-line-down" className="w-9 sm:w-[66px]">
            <span className="hidden sm:flex">Weak</span>
          </Badge>
        ) : (
          <RecommendationBadge recommendation={Recommendation.TopPick} className="sm:w-[66px]" />
        )}
      </div>
    )
  } else {
    // Draw - show all apps that tied (or all apps if no ratings)
    // If focus exists but is not in winners, show only focus app with weak badge
    const focusNotInWinners = focus && !winners.some((app) => app.id === focus.id)

    if (focusNotInWinners) {
      const { backgroundColor } = getBrandColors(focus)
      const { appIcon, appGlyph } = checkAppBranding(focus)

      return (
        <div className={cn('flex items-center gap-3', className)}>
          <div className="flex items-center gap-2">
            <AppIcon
              icon={appIcon}
              glyph={appGlyph}
              name={focus.name}
              slug={linkToApp ? focus.slug : undefined}
              background={backgroundColor}
              transition={false}
              size={24}
            />
          </div>
          <Badge style="disabled" icon="arrow-triangle-line-down" className="w-9 sm:w-[66px]">
            <span className="hidden sm:flex">Weak</span>
          </Badge>
        </div>
      )
    }

    return (
      <div className={cn('flex items-center gap-3', className)}>
        <div className="flex flex-row-reverse gap-2">
          {winners.map((app) => {
            const { backgroundColor } = getBrandColors(app)
            const { appIcon, appGlyph } = checkAppBranding(app)
            return (
              <AppIcon
                key={app.id}
                icon={appIcon}
                glyph={appGlyph}
                name={app.name}
                slug={linkToApp ? app.slug : undefined}
                background={backgroundColor}
                transition={false}
                size={24}
                className={cn(focus && focus?.name !== app.name ? 'hidden' : 'flex')}
              />
            )
          })}
        </div>
        <Badge style="outline" icon="weak" className="w-9 sm:w-[66px]">
          <span className="hidden sm:flex">Draw</span>
        </Badge>
      </div>
    )
  }
}

// Exported for backwards compatibility - used in Editor's Verdict section (CompareContainer)
// This component shows apps with a criterion without comparing ratings
export function CriterionOutcome({
  apps,
  criterionId,
  categoryId,
  appCategoryEfficiencyPoints,
  focus,
}: {
  apps: App[]
  criterionId?: Criterion['id']
  categoryId?: Category['id']
  appCategoryEfficiencyPoints: Record<App['id'], number | undefined>
  focus?: string | null | undefined
}) {
  const sortedApps = [...apps].sort(
    (a, b) => (appCategoryEfficiencyPoints[b.id] ?? 0) - (appCategoryEfficiencyPoints[a.id] ?? 0),
  )

  const { winners, isDraw } =
    criterionId !== undefined && categoryId !== undefined
      ? determineWinner(sortedApps, criterionId, categoryId)
      : { winners: sortedApps, isDraw: sortedApps.length > 1 }

  const focusedApp = apps.find((app) => app.name === focus)

  return <CriterionOutcomeDisplay winners={winners} isDraw={isDraw} focus={focusedApp} />
}
